// Strona pojedynczego artykułu bloga – /blog/:slug
// Logika:
//  1. Wywołaj URL resolver z urlKey = "blog/{slug}"
//     - sukces + page_type === "ARTICLE_BLOG" → pobierz artykuł po params (entity_id)
//  2. Jeśli URL resolver zwróci 404 i slug jest numeryczny → pobierz artykuł bezpośrednio po ID
//  3. W pozostałych przypadkach → <NoMatch />

import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { reduxActions, useDispatch } from 'store';
import { useGetLayoutUrlResolver, useGetKnowledgeBaseArticle } from 'api';
import { getTranslationByLanguage } from 'api/helpers';
import { Container, Breadcrumbs, Loader } from 'components/controls';
import { CalendarIcon } from 'assets/icons';
import { NoMatch } from 'pages';

import styles from 'theme/pages/BlogArticle/BlogArticle.module.scss';

const BlogArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const slugIsNumeric = /^\d+$/.test(slug || '');
  const slugAsNumber = slugIsNumeric ? parseInt(slug || '0', 10) : 0;

  // ── 1. URL resolver ────────────────────────────────────────────────────────
  const {
    data: urlResolverData,
    status: resolverStatus,
    error: resolverError
  } = useGetLayoutUrlResolver(
    { urlKey: slug || '' },
    { retry: false }
  );

  const resolverNotFound = resolverStatus === 'error' && (resolverError as any)?.httpStatus === 404;
  const resolverSuccess = resolverStatus === 'success' && urlResolverData?.page_type === 'ARTICLE_BLOG';

  // entity_id from URL resolver params
  const articleIdFromResolver = useMemo(() => {
    if (!resolverSuccess || !urlResolverData?.parameters) return 0;
    const parsed = parseInt(urlResolverData.parameters, 10);
    return isNaN(parsed) ? 0 : parsed;
  }, [resolverSuccess, urlResolverData]);

  // ── 2. Article fetch ───────────────────────────────────────────────────────
  // Fetch by resolver entity_id OR by numeric slug (fallback)
  const articleId = resolverSuccess
    ? articleIdFromResolver
    : resolverNotFound && slugIsNumeric
    ? slugAsNumber
    : 0;

  const shouldFetch = articleId > 0;

  const { data: article, isLoading: isArticleLoading } = useGetKnowledgeBaseArticle(
    articleId,
    { enabled: shouldFetch, retry: false }
  );

  // ── Breadcrumbs ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!article) return;
    dispatch(
      reduxActions.setBreadcrumbs([
        { name: t('Baza wiedzy'), path: '/knowledge-base' },
        { name: article.title || '', path: undefined }
      ])
    );
  }, [article]);

  // ── SEO from URL resolver ──────────────────────────────────────────────────
  const seoData = useMemo(() => {
    if (!urlResolverData) return null;
    const lang = i18n.language;
    return {
      title: getTranslationByLanguage(urlResolverData.meta_title, lang),
      description: getTranslationByLanguage(urlResolverData.meta_description, lang),
      keywords: getTranslationByLanguage(urlResolverData.meta_keywords, lang),
      ogTitle: getTranslationByLanguage(urlResolverData.open_graph_title, lang),
      ogDescription: getTranslationByLanguage(urlResolverData.open_graph_description, lang),
      ogImage: urlResolverData.open_graph_image
    };
  }, [urlResolverData, i18n.language]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getArticleData = (article: any) => {
    const fields = article.article_fields || {};
    const image = fields['image'] as string | null;
    const subtitle = fields['podtytuł'] || fields['Podtytuł'] || fields['subtitle'] || '';
    const content = fields['content'] || '';
    const date = article.sys_insert_date
      ? new Date(article.sys_insert_date).toLocaleDateString('pl-PL')
      : '';
    return { image, subtitle, content, date };
  };

  // ── Render states ──────────────────────────────────────────────────────────

  // Still waiting for resolver (and we haven't decided yet)
  if (resolverStatus === 'loading') {
    return <Loader />;
  }

  // Resolver returned 404 and slug is not numeric → 404
  if (resolverNotFound && !slugIsNumeric) {
    return <NoMatch />;
  }

  // Waiting for article
  if (shouldFetch && isArticleLoading) {
    return <Loader />;
  }

  // Article not found after all attempts
  if (!article) {
    return <NoMatch />;
  }

  const { image, subtitle, content, date } = getArticleData(article);

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-BlogArticle')}>
      <Helmet>
        <title>{seoData?.title || article.title}</title>
        {seoData?.description && <meta name="description" content={seoData.description} />}
        {seoData?.keywords && <meta name="keywords" content={seoData.keywords} />}
        {seoData?.ogTitle && <meta property="og:title" content={seoData.ogTitle} />}
        {seoData?.ogDescription && (
          <meta property="og:description" content={seoData.ogDescription} />
        )}
        {seoData?.ogImage && <meta property="og:image" content={seoData.ogImage} />}
        <meta property="og:url" content={window.location.href} />
        <link rel="canonical" href={window.location.href} />
        {urlResolverData?.href_lang?.map((shortcut, i) => (
          <link
            key={i}
            rel="alternate"
            href={`${window.location.origin}/${shortcut.language}/${shortcut.url_shortcut.replace(/^\//, '')}`}
            hrefLang={shortcut.language}
          />
        ))}
      </Helmet>

      <Breadcrumbs />

      <Container>
        <div className={styles.articleContent}>
          {image && (
            <div className={styles.imageWrapper}>
              <img className={styles.image} src={image} alt={article.title} />
            </div>
          )}

          <h1 className={styles.title}>{article.title}</h1>

          <div className={styles.lineSeparator} />

          {date && (
            <div className={styles.meta}>
              <span className={styles.date}>
                <CalendarIcon className={styles.calendarIcon} />
                {date}
              </span>
            </div>
          )}

          {subtitle && (
            <h2 className={styles.subtitle} dangerouslySetInnerHTML={{ __html: subtitle }} />
          )}

          <article className={styles.article}>
            <div className={styles.contentWrapper}>
              {content && (
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              )}
            </div>
          </article>
        </div>
      </Container>
    </div>
  );
};

export default BlogArticle;
